import { useState } from 'react'
import { Icon } from '@iconify/react'
import { toast } from 'react-toastify'
import Card from '../components/common/Card'
import PageContainer from '../components/layout/PageContainer'
import Button from '../components/common/Button'
import ConfirmDialog from '../components/common/ConfirmDialog'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { useLicense } from '../hooks/useLicense'

const REASON_LABELS = {
  invalid_key: 'Invalid license key',
  license_revoked: 'License has been revoked',
  account_inactive: 'Account is inactive',
  no_subscription: 'No active subscription',
  subscription_inactive: 'Subscription is inactive',
  subscription_expired: 'Subscription has expired',
  not_activated: 'Not activated on this site',
  activation_limit_reached: 'Activation limit reached',
}

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—'

const ApiManagement = () => {
  const { license, isLoading, error, refetch, regenerate, isRegenerating, removeActivation, isRemovingActivation } =
    useLicense()
  const [keyVisible, setKeyVisible] = useState(false)
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false)
  const [activationToRemove, setActivationToRemove] = useState(null)

  const licenseKey = license?.licenseKey ?? ''
  const sites = license?.activations ?? []
  const maxSlots = license?.maxActivations
  const isValid = license?.valid === true

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(licenseKey)
      toast.success('License key copied to clipboard!')
    } catch {
      toast.error('Could not copy the license key')
    }
  }

  const handleRegenerate = async () => {
    try {
      await regenerate()
      setKeyVisible(false)
      toast.success('License key regenerated. All sites have been deactivated.')
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to regenerate license key'))
    } finally {
      setShowRegenerateDialog(false)
    }
  }

  const handleRemoveActivation = async () => {
    try {
      await removeActivation(activationToRemove)
      toast.success('Site removed successfully!')
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to remove site'))
    } finally {
      setActivationToRemove(null)
    }
  }

  return (
    <PageContainer title="Plugin License" subtitle="Manage your plugin license key and activated sites">
      <Card title="Plugin License" className="mb-6">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-red-600">{getErrorMessage(error, 'Failed to load license')}</p>
            <Button variant="secondary" size="sm" icon="mdi:refresh" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-3 sm:p-4 bg-bg-light rounded-lg">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <code className="font-mono text-xs sm:text-sm bg-white px-2 py-1.5 sm:py-1 rounded border border-border-light break-all flex-1 min-w-0">
                  {keyVisible ? licenseKey : '•'.repeat(Math.min(licenseKey.length || 26, 30))}
                </code>
                <button
                  onClick={() => setKeyVisible((visible) => !visible)}
                  className="text-text-secondary hover:text-primary transition-colors flex-shrink-0 p-1"
                  aria-label={keyVisible ? 'Hide key' : 'Show key'}
                >
                  <Icon icon={keyVisible ? 'mdi:eye-off' : 'mdi:eye'} className="text-lg sm:text-xl" />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-text-secondary">
                <span
                  className={`font-medium ${isValid ? 'text-green-600' : 'text-red-600'}`}
                >
                  {isValid ? 'Valid' : REASON_LABELS[license?.reason] || license?.message || 'Not valid'}
                </span>
                {license?.planName && <span>Plan: {license.planName}</span>}
                {license?.validUntil && <span>Valid until {formatDate(license.validUntil)}</span>}
                {maxSlots !== undefined && (
                  <span>
                    Sites: {sites.length}/{maxSlots}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  icon="mdi:content-copy"
                  onClick={handleCopy}
                  disabled={!licenseKey}
                >
                  Copy
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon="mdi:refresh"
                  onClick={() => setShowRegenerateDialog(true)}
                  disabled={isRegenerating}
                >
                  Regenerate
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-text-dark mb-2">Activated Sites</h3>
              {sites.length === 0 ? (
                <p className="text-sm text-text-secondary">
                  No sites activated yet. Enter this key in the plugin settings to activate a site.
                </p>
              ) : (
                <div className="space-y-2">
                  {sites.map((site) => {
                    const id = site.id ?? site._id ?? site.activationId
                    return (
                      <div
                        key={id}
                        className="flex items-center justify-between gap-3 p-3 bg-bg-light rounded-lg"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-text-dark break-all">{site.siteUrl}</p>
                          <p className="text-xs text-text-secondary">
                            Activated on {formatDate(site.activatedAt ?? site.createdAt)}
                            {site.pluginVersion ? ` • Plugin v${site.pluginVersion}` : ''}
                          </p>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setActivationToRemove(id)}
                          disabled={isRemovingActivation}
                        >
                          Remove
                        </Button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      <ConfirmDialog
        isOpen={showRegenerateDialog}
        onClose={() => setShowRegenerateDialog(false)}
        onConfirm={handleRegenerate}
        title="Regenerate License Key"
        message="The current key will stop working and all activated sites will be deactivated. You will need to enter the new key in each plugin."
        confirmText="Regenerate"
        cancelText="Cancel"
        variant="danger"
        icon="mdi:key-remove"
      />

      <ConfirmDialog
        isOpen={activationToRemove !== null}
        onClose={() => setActivationToRemove(null)}
        onConfirm={handleRemoveActivation}
        title="Remove Site"
        message="This frees the activation slot. The plugin on that site will stop working until it is activated again."
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        icon="mdi:link-off"
      />
    </PageContainer>
  )
}

export default ApiManagement
